import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import axios from 'axios'
import DateRangeSelector, { Range } from '../components/DateRangeFilter'
import { format, subDays } from 'date-fns'
import { CostByServicePie } from '../components/CostByServicePie'
import { ServiceCostTable } from '../components/ServiceCostTable'
import { EmptyState } from '../components/EmptyState'
import { TrendChart } from '../components/TrendChart'

interface CostSummary {
  totalCost: number
  previousPeriodCost: number
  changePercent: number
  recordCount: number
  period: {
    start: string
    end: string
    days: number
  }
}

interface DailyCost {
  date: string
  cost: number
}

interface ServiceBreakdown {
  service: string
  cost: number
  percentage: number
}

interface Alert {
  id: number
  title: string
  type: string
  severity: string
  status: string
  service?: string
  accountId?: string
  detectedAt: string
  metadata?: {
    currentCost?: number
    baselineCost?: number
    costIncrease?: number
    percentageIncrease?: number
    [key: string]: any
  }
}

export default function DashboardPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [costSummary, setCostSummary] = useState<CostSummary | null>(null)
  const [dailyCosts, setDailyCosts] = useState<DailyCost[]>([])
  const [serviceBreakdown, setServiceBreakdown] = useState<ServiceBreakdown[]>([])
  const [recentAlerts, setRecentAlerts] = useState<Alert[]>([])
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState<Range>({
    start: subDays(new Date(), 29),
    end: new Date(),
    preset: 30
  })

  useEffect(() => {
    loadDashboardData()
  }, [dateRange])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('finops_token')
      const headers = { Authorization: `Bearer ${token}` }

      const startStr = format(dateRange.start, 'yyyy-MM-dd')
      const endStr = format(dateRange.end, 'yyyy-MM-dd')

      const [summaryRes, breakdownRes, alertsRes] = await Promise.all([
        axios.get(`/api/costs/summary?start=${startStr}&end=${endStr}`, { headers }),
        axios.get(`/api/costs/breakdown?start=${startStr}&end=${endStr}&groupBy=service`, {
          headers
        }),
        axios.get('/api/alerts?limit=5&status=open', { headers })
      ])

      setCostSummary(summaryRes.data.summary)
      setDailyCosts(summaryRes.data.dailyCosts)
      setServiceBreakdown(breakdownRes.data.breakdown)
      setRecentAlerts(alertsRes.data.alerts)
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value)
  }

  const getChangeClass = (change: number) => {
    if (change > 0) return 'text-red-600'
    if (change < 0) return 'text-green-600'
    return 'text-gray-600'
  }

  const handleDateRangeChange = (range: Range) => {
    setDateRange(range)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <p className="ml-4 text-gray-600">Loading dashboard...</p>
      </div>
    )
  }

  // Filter services with non-zero cost or percentage and limit to top 10
  const filteredServiceBreakdown = serviceBreakdown
    .filter((service) => service.cost > 0 && service.percentage > 0)
    .sort((a, b) => b.cost - a.cost)
    .slice(0, 10)

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">Welcome back, {user?.name}</p>
        </div>
        <DateRangeSelector value={dateRange} onChange={handleDateRangeChange} />
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Cost */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-500">Total Cost</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(costSummary?.totalCost || 0)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Last {costSummary?.period.days || 30} days
              </p>
            </div>
          </div>
        </div>

        {/* Change */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                (costSummary?.changePercent || 0) > 0
                  ? 'bg-red-100'
                  : (costSummary?.changePercent || 0) < 0
                    ? 'bg-green-100'
                    : 'bg-gray-100'
              }`}
            >
              <svg
                className={`w-6 h-6 ${
                  (costSummary?.changePercent || 0) > 0
                    ? 'text-red-600'
                    : (costSummary?.changePercent || 0) < 0
                      ? 'text-green-600'
                      : 'text-gray-600'
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-500">Change</p>
              <p
                className={`text-2xl font-bold ${getChangeClass(costSummary?.changePercent || 0)}`}
              >
                {(costSummary?.changePercent || 0) >= 0 ? '+' : ''}
                {(costSummary?.changePercent || 0).toFixed(1)}%
              </p>
              <p className="text-xs text-gray-500 mt-1">vs previous period</p>
            </div>
          </div>
        </div>

        {/* Active Alerts */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                recentAlerts.length > 0 ? 'bg-red-100' : 'bg-green-100'
              }`}
            >
              <svg
                className={`w-6 h-6 ${recentAlerts.length > 0 ? 'text-red-600' : 'text-green-600'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-500">Active Alerts</p>
              <p
                className={`text-2xl font-bold ${recentAlerts.length > 0 ? 'text-red-600' : 'text-green-600'}`}
              >
                {recentAlerts.length}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {recentAlerts.length > 0 ? 'Requires attention' : 'All clear'}
              </p>
            </div>
          </div>
        </div>

        {/* Daily Average */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-amber-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-500">Daily Average</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(
                  (costSummary?.totalCost || 0) / Math.max(costSummary?.period.days || 1, 1)
                )}
              </p>
              <p className="text-xs text-gray-500 mt-1">Per day average</p>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Cost Trend */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6">
          {!dailyCosts || dailyCosts.length === 0 ? (
            <EmptyState
              icon="chart"
              title="No daily cost data available"
              description="Cost trend data will appear here once usage data is processed."
              className="h-64"
            />
          ) : (
            <TrendChart
              data={dailyCosts}
              dataKey="cost"
              xAxisKey="date"
              title="Daily Cost Trend"
              color="#2563eb"
              height={320}
              valueFormatter={formatCurrency}
              defaultChartType="area"
              enableToggle={true}
              dateRange={dateRange}
            />
          )}
        </div>
      </div>

      {/* Cost by Service - Two Column Layout */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Pie chart */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Services by Cost</h3>
          <div className="h-80 flex items-center justify-center">
            <CostByServicePie data={filteredServiceBreakdown} />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Cost Breakdown</h3>
          <div className="flex-1 overflow-auto max-h-80">
            <ServiceCostTable data={filteredServiceBreakdown} />
          </div>
        </div>
      </div>

      {/* Recent Alerts */}
      {recentAlerts.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Recent Alerts</h3>
            <button
              onClick={() => navigate('/alerts')}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View all →
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {recentAlerts.map((alert) => {
              const costImpact = alert.metadata?.currentCost || alert.metadata?.costIncrease || 0
              return (
                <div
                  key={alert.id}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors flex items-center justify-between gap-4"
                  onClick={() => navigate('/alerts')}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{alert.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {alert.service || 'Multiple services'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        alert.severity === 'critical'
                          ? 'bg-red-100 text-red-800'
                          : alert.severity === 'high'
                            ? 'bg-amber-100 text-amber-800'
                            : alert.severity === 'medium'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {alert.severity}
                    </span>
                    <span className="text-sm font-medium text-gray-900 w-24 text-right">
                      {costImpact > 0 ? formatCurrency(costImpact) : '—'}
                    </span>
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
